import React, { useState, useEffect } from 'react';
import Joyride, { CallBackProps, STATUS, Step } from 'react-joyride';
import { useAppSelector } from '../../hooks/useRedux';

interface OnboardingTourProps {
    isFirstTimeUser: boolean;
    onComplete: () => void;
}

const OnboardingTour: React.FC<OnboardingTourProps> = ({ isFirstTimeUser, onComplete }) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [steps, setSteps] = useState<Step[]>([]);
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [lastStepIndex, setLastStepIndex] = useState<number>(0);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Function to highlight the current step's target element
    const highlightCurrentTarget = (target: string) => {
        // Remove previous highlights
        document.querySelectorAll('.tour-highlight').forEach(el => {
            el.classList.remove('tour-highlight');
        });

        // Add highlight to current target
        const targetElement = document.querySelector(target);
        if (targetElement) {
            targetElement.classList.add('tour-highlight');
        }
    };

    useEffect(() => {
        // Add CSS for highlighted elements
        const style = document.createElement('style');
        style.innerHTML = `
            .tour-highlight {
                box-shadow: 0 0 0 3px rgba(67, 100, 247, 0.5) !important;
                border-radius: 4px !important;
                transition: all 0.3s ease-in-out !important;
                background-color: rgba(255, 255, 255, 0.95) !important;
                color: #4364F7 !important;
                position: relative !important;
                z-index: 10 !important;
                animation: pulse-highlight 1.5s infinite alternate !important;
            }
            
            .tour-highlight svg {
                color: #4364F7 !important;
            }
            
            .tour-highlight span {
                color: #4364F7 !important;
                font-weight: 600 !important;
            }
            
            @keyframes pulse-highlight {
                0% {
                    box-shadow: 0 0 0 3px rgba(67, 100, 247, 0.5) !important;
                }
                100% {
                    box-shadow: 0 0 0 6px rgba(67, 100, 247, 0.3) !important;
                }
            }
            
            /* Special handling for dark mode */
            .dark .tour-highlight {
                background-color: rgba(44, 55, 74, 0.95) !important;
                color: #4364F7 !important;
            }
        `;
        document.head.appendChild(style);

        return () => {
            document.head.removeChild(style);
        };
    }, []);

    useEffect(() => {
        // Wait for DOM to be ready
        setTimeout(() => {
            // Dynamically initialize steps after DOM is fully loaded
            const newSteps: Step[] = [];
            
            // First step - Search input
            const searchInput = document.querySelector('#search');
            if (searchInput) {
                newSteps.push({
                    target: '#search',
                    content: 'Welcome to AtomPass! Search for any item using the search bar.',
                    placement: isMobile ? 'bottom' : 'bottom',
                    disableBeacon: true,
                    spotlightClicks: true,
                });
            }
            
            // Check for all the sidebar navigation links and add them if they exist
            const allItemsLink = document.querySelector('a[href="/all-items"]');
            if (allItemsLink) {
                newSteps.push({
                    target: 'a[href="/all-items"]',
                    content: 'View all your stored items in one place.',
                    placement: 'right',
                    spotlightClicks: true,
                });
            }

            const vaultsLink = document.querySelector('a[href="/vaults"]');
            if (vaultsLink) {
                newSteps.push({
                    target: 'a[href="/vaults"]',
                    content: 'Organize your items in different vaults.',
                    placement: 'right',
                    spotlightClicks: true,
                });
            }

            const favoritesLink = document.querySelector('a[href="/favorites"]');
            if (favoritesLink) {
                newSteps.push({
                    target: 'a[href="/favorites"]',
                    content: 'Access your most used items quickly from favorites.',
                    placement: 'right',
                    spotlightClicks: true,
                });
            }

            const passwordsLink = document.querySelector('a[href="/passwords"]');
            if (passwordsLink) {
                newSteps.push({
                    target: 'a[href="/passwords"]',
                    content: 'Securely store all your passwords.',
                    placement: 'right',
                    spotlightClicks: true,
                });
            }

            const cardDetailsLink = document.querySelector('a[href="/card-details"]');
            if (cardDetailsLink) {
                newSteps.push({
                    target: 'a[href="/card-details"]',
                    content: 'Store and manage your credit card information securely.',
                    placement: 'right',
                    spotlightClicks: true,
                });
            }

            const bankDetailsLink = document.querySelector('a[href="/bank-details"]');
            if (bankDetailsLink) {
                newSteps.push({
                    target: 'a[href="/bank-details"]',
                    content: 'Keep your bank account details secure and easily accessible.',
                    placement: 'right',
                    spotlightClicks: true,
                });
            }

            const personalDetailsLink = document.querySelector('a[href="/personal-details"]');
            if (personalDetailsLink) {
                newSteps.push({
                    target: 'a[href="/personal-details"]',
                    content: 'Store personal identification information securely.',
                    placement: 'right',
                    spotlightClicks: true,
                });
            }

            // Teams section
            const teamsLink = document.querySelector('a[href="/teams"]');
            if (teamsLink) {
                newSteps.push({
                    target: 'a[href="/teams"]',
                    content: 'Collaborate with team members by sharing secure information.',
                    placement: 'right',
                    spotlightClicks: true,
                });
            }

            // Sharing Center
            const sharingCenterLink = document.querySelector('a[href="/sharing-center"]');
            if (sharingCenterLink) {
                newSteps.push({
                    target: 'a[href="/sharing-center"]',
                    content: 'Manage all your shared items and permissions in one place.',
                    placement: 'right',
                    spotlightClicks: true,
                });
            }

            // Check for password generator and dark mode toggle buttons
            const passwordGenerator = document.querySelector('[aria-label="Password Generator"]');
            if (passwordGenerator) {
                newSteps.push({
                    target: '[aria-label="Password Generator"]',
                    content: 'Generate secure, random passwords with our built-in tool.',
                    placement: isMobile ? 'bottom' : 'left',
                    spotlightClicks: true,
                });
            }

            const darkModeToggle = document.querySelector('[aria-label="Dark Mode Toggle"]');
            if (darkModeToggle) {
                newSteps.push({
                    target: '[aria-label="Dark Mode Toggle"]',
                    content: 'Toggle between light and dark mode for comfortable viewing.',
                    placement: isMobile ? 'bottom' : 'left',
                    spotlightClicks: true,
                });
            }

            // Profile section
            const profileButton = document.querySelector('.profile-dropdown button');
            if (profileButton) {
                newSteps.push({
                    target: '.profile-dropdown button',
                    content: 'Access your profile settings, switch between accounts, or log out.',
                    placement: 'bottom',
                    spotlightClicks: true,
                });
            }

            const settingsLink = document.querySelector('a[href="/settings"]');
            if (settingsLink) {
                newSteps.push({
                    target: 'a[href="/settings"]',
                    content: 'Configure your application preferences and account settings.',
                    placement: 'right',
                    spotlightClicks: true,
                });
            }

            setSteps(newSteps);
            setLastStepIndex(newSteps.length - 1);
        }, 1000); // Wait for DOM to be fully rendered
    }, [isMobile]);

    const handleJoyrideCallback = (data: CallBackProps) => {
        const { status, type, index } = data;
        
        if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
            // Remove highlight from all elements when tour ends
            document.querySelectorAll('.tour-highlight').forEach(el => {
                el.classList.remove('tour-highlight');
            });
            onComplete();
        }

        // Handle step changes
        if (type === 'step:before') {
            setCurrentStep(index);
            
            // Apply highlight to the current target
            if (data.step && data.step.target) {
                highlightCurrentTarget(data.step.target as string);
            }
        }

        if (type === 'step:after') {
            // Scroll the target into view if it's not visible
            const targetElement = document.querySelector(data.step.target as string);
            if (targetElement) {
                targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    };

    if (!isFirstTimeUser || steps.length === 0) {
        return null;
    }

    return (
        <Joyride
            steps={steps}
            continuous
            showProgress
            showSkipButton
            callback={handleJoyrideCallback}
            locale={{
                last: 'Finish'
            }}
            styles={{
                options: {
                    primaryColor: '#4364F7',
                    zIndex: 1000,
                    arrowColor: '#fff',
                },
                tooltip: {
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    padding: '16px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    fontSize: isMobile ? '14px' : '16px',
                    maxWidth: isMobile ? '300px' : '400px',
                },
                tooltipTitle: {
                    fontSize: isMobile ? '16px' : '18px',
                    fontWeight: '600',
                    marginBottom: '8px',
                },
                buttonNext: {
                    backgroundColor: '#4364F7',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    fontSize: isMobile ? '14px' : '16px',
                },
                buttonBack: {
                    color: '#4364F7',
                    marginRight: '8px',
                    fontSize: isMobile ? '14px' : '16px',
                },
                buttonSkip: {
                    color: '#6B7280',
                    fontSize: isMobile ? '14px' : '16px',
                },
                overlay: {
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                },
                spotlight: {
                    backgroundColor: 'transparent',
                },
            }}
            floaterProps={{
                disableAnimation: true,
                styles: {
                    floater: {
                        filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))',
                    },
                },
            }}
        />
    );
};

export default OnboardingTour; 